/**
 * Camera utility for Native Browser API camera operations.
 * Replaces react-webcam with direct navigator.mediaDevices.getUserMedia usage.
 */

export interface CameraConstraints {
  height: { ideal: number };
  width: { ideal: number };
  facingMode: 'environment' | 'user';
}

export interface CameraInterface {
  stream: MediaStream | null;
  videoElement: HTMLVideoElement | null;
  initialize: (videoElement: HTMLVideoElement, constraints: CameraConstraints) => Promise<void>;
  takePhoto: (quality?: number) => Promise<{ file: File; dataUrl: string } | null>;
  close: () => void;
}

const DEFAULT_CONSTRAINTS: CameraConstraints = {
  height: { ideal: 2988 },
  width: { ideal: 5312 },
  facingMode: 'environment',
};

/**
 * Creates a camera interface for managing camera stream and photo capture.
 */
export const createCameraInterface = (): CameraInterface => {
  let stream: MediaStream | null = null;
  let videoElement: HTMLVideoElement | null = null;

  /**
   * Initialize camera stream and attach to video element.
   */
  const initialize = async (video: HTMLVideoElement, constraints: CameraConstraints = DEFAULT_CONSTRAINTS): Promise<void> => {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error('NotSupportedError: Camera API not supported');
    }
    try {
      try {
        // The first choice is back camera, but new Tablet(Dell or Lenovo) require "exact" to use back camera
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: constraints.width,
            height: constraints.height,
            facingMode: { exact: constraints.facingMode },
          },
          audio: false,
        });
      } catch {
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: constraints.width,
            height: constraints.height,
            facingMode: constraints.facingMode,
          },
          audio: false,
        });
      }

      videoElement = video;

      // Wait for video to be ready
      await new Promise<void>((resolve, reject) => {
        const cleanup = () => {
          video.onloadedmetadata = null;
          video.onerror = null;
        };
        const handleMetadataLoaded = () => {
          cleanup();
          video.play().then(resolve).catch(reject);
        };
        const handleError = () => {
          cleanup();
          reject(new Error('Failed to load video'));
        };

        video.onloadedmetadata = handleMetadataLoaded;
        video.onerror = handleError;
        video.srcObject = stream;

        if (video.readyState >= 1) {
          handleMetadataLoaded();
        }
      });
    } catch (error) {
      close();
      throw error;
    }
  };

  /**
   * Capture photo from video stream using Canvas API.
   * Returns full resolution JPEG image.
   * @param quality - JPEG quality from 0 to 1. Default is 0.95.
   */
  const takePhoto = async (quality: number = 0.98): Promise<{ file: File; dataUrl: string } | null> => {
    if (!videoElement || !stream) {
      return null;
    }

    const canvas = document.createElement('canvas');
    const videoWidth = videoElement.videoWidth;
    const videoHeight = videoElement.videoHeight;

    canvas.width = videoWidth;
    canvas.height = videoHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to get canvas context');
    }

    ctx.drawImage(videoElement, 0, 0, videoWidth, videoHeight);

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to capture image'));
            return;
          }

          const file = new File([blob], 'camera.jpg', {
            type: 'image/jpeg',
            lastModified: Date.now(),
          });

          const dataUrl = canvas.toDataURL('image/jpeg', quality);
          resolve({ file, dataUrl });
        },
        'image/jpeg',
        quality,
      );
    });
  };

  /**
   * Close camera stream and release resources.
   */
  const close = (): void => {
    if (stream) {
      stream.getTracks().forEach((track) => {
        track.stop();
      });
      stream = null;
    }

    if (videoElement) {
      videoElement.srcObject = null;
      videoElement = null;
    }
  };

  return {
    get stream() {
      return stream;
    },
    get videoElement() {
      return videoElement;
    },
    initialize,
    takePhoto,
    close,
  };
};

export const DEFAULT_VIDEO_CONSTRAINTS: CameraConstraints = DEFAULT_CONSTRAINTS;

/**
 * Parse camera errors from Native Browser API (navigator.mediaDevices.getUserMedia)
 * and return user-friendly Japanese messages.
 */
export const parseCameraError = (error: string): string => {
  if (error.includes('NotAllowedError') || error.includes('Permission denied')) {
    return 'カメラの起動権限がありません';
  }

  if (error.includes('NotReadableError') || error.includes('Device in use')) {
    return '他のアプリケーションでカメラを使用中です';
  }

  if (error.includes('NotFoundError') || error.includes('Requested device not found')) {
    return 'カメラが見つかりません';
  }

  if (error.includes('NotSupportedError') || error.includes('Camera API not supported')) {
    return 'お使いのブラウザはカメラ機能をサポートしていません';
  }

  if (error.includes('OverconstrainedError')) {
    return 'カメラの設定が対応していません';
  }

  if (error.includes('AbortError')) {
    return 'カメラの起動が中断されました';
  }

  if (error.includes('SecurityError')) {
    return 'セキュリティエラー: HTTPSが必要です';
  }

  return error;
};
